import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { IAudioPlayer } from './interfaces';
import { BufferLoader } from './BufferLoader';

export const AudioPlayer: FC<IAudioPlayer> = ({
  startTime,
  endTime,
  noiseEvents,
  isPlaying,
  position,
  playbackSpeed = 1,
  languageData,
}) => {
  const audioContext: AudioContext = useMemo(() => new AudioContext(), []);
  const [, updatePlayingList] = useState({});
  const sourcePlaying = useRef<Record<number, boolean>>({});
  const audioBuffer = useRef<Record<number, AudioBuffer>>({});
  const sources = useRef<Record<number, AudioBufferSourceNode>>({});
  const timing = useRef<Record<number, { startTime: number; endTime: number }>>({});
  // Anchor of when each source started playing, so we can tell an ordinary
  // playback tick (the clock advancing ~60x/sec) from a genuine seek. Without
  // this we restart the buffer source on every tick, which splices the
  // waveform and produces an audible crackle (ALN-1684).
  const playAnchor = useRef<Record<number, { contextTime: number; position: number }>>({});

  // A position change smaller than this (in seconds) is treated as the clock
  // advancing during normal playback, not a deliberate scrub. It must comfortably
  // exceed a single animation frame's worth of advance at the fastest playback
  // speed, while staying well below a plausible manual seek.
  const SEEK_TOLERANCE_SECONDS = 0.5;

  // Cleanup effect
  useEffect(() => {
    return () => {
      stopAll();
    };
  }, []);

  const addSource = (id: number) => {
    sources.current[id] = audioContext.createBufferSource();
    sources.current[id].buffer = audioBuffer.current[id];
    sources.current[id].playbackRate.value = playbackSpeed;
    sources.current[id].connect(audioContext.destination);
  };

  const playSound = (id: number, delay: number, start: number) => {
    addSource(id);
    sources.current[id].start(delay, start);
    sourcePlaying.current[id] = true;
    playAnchor.current[id] = { contextTime: audioContext.currentTime, position: start };
    updatePlayingList({ ...sourcePlaying.current });
  };

  const stopSound = (id: number) => {
    if (sourcePlaying.current[id]) {
      sources.current[id].stop();
      sourcePlaying.current[id] = false;
      updatePlayingList({ ...sourcePlaying.current });
    }

    // create a new source so it's connected already
    addSource(id);
  };

  const finishedLoading = (bufferListLoaded: any) => {
    Object.values(bufferListLoaded).forEach(({ id, buffer }: any) => {
      audioBuffer.current[id] = buffer;
    });
  };

  // True when the incoming offset differs from where the source should be by
  // now (given how long it has been playing) by more than the tolerance — i.e.
  // the user scrubbed, rather than the clock simply ticking forward.
  const isSeek = (id: number, offset: number) => {
    const anchor = playAnchor.current[id];
    if (!anchor) {
      return true;
    }
    const elapsed = (audioContext.currentTime - anchor.contextTime) * playbackSpeed;
    const expectedOffset = anchor.position + elapsed;
    return Math.abs(offset - expectedOffset) > SEEK_TOLERANCE_SECONDS;
  };

  const stopAll = () => {
    Object.entries(sourcePlaying.current).forEach(([id, playing]) => {
      if (playing) {
        stopSound(Number(id));
      }
    });
  };

  // setup audio context and buffer
  useEffect(() => {
    stopAll();
    sourcePlaying.current = {};
    noiseEvents.forEach(({ id, startTime, endTime }) => {
      const convertedStartTime = DateTime.fromISO(startTime).toSeconds();
      const convertedEndTime = DateTime.fromISO(endTime).toSeconds();
      timing.current[id] = { startTime: convertedStartTime, endTime: convertedEndTime };
      sourcePlaying.current[id] = false;
    });
    updatePlayingList({ ...sourcePlaying.current });

    const extractAudioFilesInfo = noiseEvents.map(
      ({ id, resourceUri: { authenticationScheme, uri } }) => {
        return {
          id,
          authenticationScheme,
          uri,
        };
      }
    );

    const bufferLoader: any = new BufferLoader(
      audioContext,
      extractAudioFilesInfo,
      finishedLoading
    );

    bufferLoader.load(languageData);
  }, [noiseEvents]);

  useEffect(() => {
    if (isPlaying) {
      playAudio();
    }
  }, [position]);

  useEffect(() => {
    if (!isPlaying) {
      stopAll();
    }
  }, [isPlaying]);

  const playAudio = () => {
    Object.entries(sourcePlaying.current).forEach(([id, playing]) => {
      const numId = Number(id);
      const offset = position - timing.current[id].startTime;
      if (!playing) {
        if (position >= timing.current[id].startTime && position <= timing.current[id].endTime) {
          playSound(numId, 0, offset);
        }
      } else {
        if (position > timing.current[id].endTime || position < timing.current[id].startTime) {
          stopSound(numId);
        } else if (isSeek(numId, offset)) {
          // Deliberate scrub: reposition the source to the new offset.
          stopSound(numId);
          playSound(numId, 0, offset);
        }
        // Otherwise the clock has merely advanced during normal playback —
        // leave the already-playing source untouched so the waveform is not
        // spliced (ALN-1684).
      }
    });

    // at the end of track, stop all players
    if (endTime === position) {
      stopAll();
    }
  };

  return null;
};
